import React, { useState, useEffect, useCallback } from "react";

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content large privacy-policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-policy-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="privacy-policy-title">Privacy Policy</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close privacy policy"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">
          <p className="policy-intro">
            <strong>Effective Date:</strong> July 10, 2025
          </p>

          <section className="policy-section">
            <h4>1. Introduction</h4>
            <p>
              At NCG Corporate (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or
              &ldquo;us&rdquo;), your privacy is important to us. This Privacy
              Policy outlines how we collect, use, disclose, and safeguard your
              information when you visit our website{" "}
              <a
                href="https://www.ncgcorporate.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.ncgcorporate.com
              </a>
              .
            </p>
          </section>

          <section className="policy-section">
            <h4>2. Information We Collect</h4>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li>
                <strong>Personal Identification Information:</strong> Name,
                email address, phone number, company name, etc.
              </li>
              <li>
                <strong>Technical Data:</strong> IP address, browser type,
                pages visited, time spent on the website, etc.
              </li>
              <li>
                <strong>Communication Data:</strong> Messages or inquiries you
                submit through contact forms or email.
              </li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>3. How We Use Your Information</h4>
            <p>We may use the information we collect for the following purposes:</p>
            <ul>
              <li>To provide and improve our services</li>
              <li>To respond to inquiries and customer service requests</li>
              <li>
                To send updates, promotional materials, or newsletters (if
                subscribed)
              </li>
              <li>To maintain security and prevent fraud</li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>4. Cookies and Tracking</h4>
            <p>
              Our website may use cookies and similar technologies to enhance
              user experience and gather analytics. You can disable cookies
              through your browser settings.
            </p>
          </section>

          <section className="policy-section">
            <h4>5. Sharing Your Information</h4>
            <p>We do not sell or rent your personal data. However, we may share your data:</p>
            <ul>
              <li>With service providers assisting in website operation</li>
              <li>If required by law or to protect our rights</li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>6. Data Security</h4>
            <p>
              We implement appropriate security measures to protect your
              personal data. However, no method of transmission over the
              internet is 100% secure.
            </p>
          </section>

          <section className="policy-section">
            <h4>7. Your Rights</h4>
            <p>You have the right to:</p>
            <ul>
              <li>Access the data we hold about you</li>
              <li>Request corrections or deletions</li>
              <li>Opt out of marketing communications</li>
            </ul>
            <p>
              To exercise these rights, contact us at{" "}
              <a href="mailto:support@ncgcorporate.com">
                support@ncgcorporate.com
              </a>
              .
            </p>
          </section>

          <section className="policy-section">
            <h4>8. Third-Party Links</h4>
            <p>
              Our website may contain links to third-party sites. We are not
              responsible for their privacy practices.
            </p>
          </section>

          <section className="policy-section">
            <h4>9. Changes to This Policy</h4>
            <p>
              We may update this Privacy Policy from time to time. Updates will
              be posted on this page with the revised date.
            </p>
          </section>

          <section className="policy-section">
            <h4>10. Contact Us</h4>
            <p>
              If you have questions about this Privacy Policy, please contact
              us at:{" "}
              <a href="mailto:support@ncgcorporate.com">
                support@ncgcorporate.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

const AccessibilityPolicyModal = ({ isOpen, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content large privacy-policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="accessibility-policy-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="accessibility-policy-title">Accessibility Policy</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close accessibility policy"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">
          <section className="policy-section">
            <h4>Commitment to Accessibility</h4>
            <p>
              At NCG Corporate Engineers Pvt. Ltd., we are committed to ensuring
              that our website is accessible to all users, including people with
              disabilities. We strive to provide a seamless and inclusive digital
              experience for every visitor.
            </p>
          </section>

          <section className="policy-section">
            <h4>Accessibility Standards</h4>
            <p>
              Our website is designed to follow internationally recognized
              accessibility standards, including:
            </p>
            <ul>
              <li>
                Web Content Accessibility Guidelines (WCAG 2.1 &ndash; Level AA)
              </li>
              <li>Guidelines for Indian Government Websites (GIGW)</li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>Accessibility Features</h4>

            <h5 className="policy-subheading">1. Perceivable Content</h5>
            <ul>
              <li>Images include alternative text (alt text)</li>
              <li>Proper color contrast for readability</li>
              <li>Structured headings and content layout</li>
            </ul>

            <h5 className="policy-subheading">2. Operable Interface</h5>
            <ul>
              <li>Full keyboard navigation support</li>
              <li>Clear and consistent website navigation</li>
              <li>No content that may cause seizures or discomfort</li>
            </ul>

            <h5 className="policy-subheading">3. Understandable Information</h5>
            <ul>
              <li>Simple and clear language</li>
              <li>Well-organized content structure</li>
              <li>Forms with proper labels and error messages</li>
            </ul>

            <h5 className="policy-subheading">4. Robust Compatibility</h5>
            <ul>
              <li>
                Compatible with assistive technologies such as screen readers
              </li>
              <li>Works across modern browsers and devices</li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>Device &amp; Browser Compatibility</h4>
            <p>The NCG website is optimized for:</p>
            <ul>
              <li>Desktop and laptop systems</li>
              <li>Mobile phones and tablets</li>
              <li>
                Major browsers including Chrome, Edge, Firefox, and Safari
              </li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>Continuous Improvement</h4>
            <p>
              We are continuously working to improve accessibility and usability.
              While we strive for full compliance, some areas may still be under
              development.
            </p>
          </section>

          <section className="policy-section">
            <h4>Third-Party Content</h4>
            <p>
              Our website may contain links to external websites. NCG Corporate
              Engineers Pvt. Ltd. is not responsible for the accessibility of
              such external content.
            </p>
          </section>

          <section className="policy-section">
            <h4>Feedback &amp; Assistance</h4>
            <p>
              If you experience any difficulty accessing content on our website,
              please contact us:
            </p>
            <ul className="policy-contact-list">
              <li>
                Email:{" "}
                <a href="mailto:info@ncgengineering.com">
                  info@ncgengineering.com
                </a>
              </li>
              <li>
                Phone:{" "}
                <a href="tel:+919779044006">+91-9779044006</a>
              </li>
            </ul>
            <p>Please include details such as:</p>
            <ul>
              <li>The page you were trying to access</li>
              <li>The issue faced</li>
              <li>Device/browser details</li>
            </ul>
            <p>
              We will make every effort to respond and resolve the issue promptly.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

const TermsOfUseModal = ({ isOpen, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content large privacy-policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-of-use-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="terms-of-use-title">Terms of Use</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close terms of use"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">
          <section className="policy-section">
            <h4>1. Acceptance of Terms</h4>
            <p>
              By accessing and using the website of NCG Corporate Engineers Pvt.
              Ltd., you agree to comply with and be bound by these Terms of Use.
              If you do not agree, please do not use this website.
            </p>
          </section>

          <section className="policy-section">
            <h4>2. About Us</h4>
            <p>
              This website is owned and operated by NCG Corporate Engineers Pvt.
              Ltd., a company engaged in manufacturing, sourcing, and exporting
              engineering and steel products including fabricated parts, stamping
              components, pipes, and assemblies.
            </p>
          </section>

          <section className="policy-section">
            <h4>3. Use of Website</h4>
            <p>
              You agree to use this website only for lawful purposes and in a way
              that does not:
            </p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe the rights of others</li>
              <li>Restrict or inhibit anyone else&apos;s use of the website</li>
            </ul>
            <p>
              Unauthorized use of this website may result in legal action.
            </p>
          </section>

          <section className="policy-section">
            <h4>4. Product Information</h4>
            <p>
              All product details, specifications, images, and descriptions
              provided on this website are for general information purposes only.
            </p>
            <ul>
              <li>
                Products are manufactured as per customer drawings,
                specifications, or samples
              </li>
              <li>Specifications may change without prior notice</li>
              <li>
                Images are for reference only and may vary from actual products
              </li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>5. Intellectual Property Rights</h4>
            <p>All content on this website, including:</p>
            <ul>
              <li>Text</li>
              <li>Images</li>
              <li>Logos</li>
              <li>Designs</li>
              <li>Documents</li>
            </ul>
            <p>
              is the property of NCG Corporate Engineers Pvt. Ltd. and is
              protected under applicable intellectual property laws.
            </p>
            <p>
              You may not copy, reproduce, or distribute any content without prior
              written permission.
            </p>
          </section>

          <section className="policy-section">
            <h4>6. Pricing &amp; Commercial Terms</h4>
            <p>
              Any pricing, quotations, or commercial terms mentioned on the
              website:
            </p>
            <ul>
              <li>Are indicative only</li>
              <li>Subject to change without notice</li>
              <li>
                Final terms will be confirmed through official quotations,
                Proforma Invoices, or agreements
              </li>
            </ul>
            <p>
              Export terms such as FOB, payment terms, and delivery timelines will
              be shared separately during business transactions.
            </p>
          </section>

          <section className="policy-section">
            <h4>7. Limitation of Liability</h4>
            <p>
              NCG Corporate Engineers Pvt. Ltd. shall not be held liable for:
            </p>
            <ul>
              <li>Any direct or indirect damages arising from use of this website</li>
              <li>Errors or omissions in content</li>
              <li>Temporary unavailability of the website</li>
            </ul>
            <p>Users rely on the information at their own discretion.</p>
          </section>

          <section className="policy-section">
            <h4>8. Third-Party Links</h4>
            <p>
              This website may include links to third-party websites for user
              convenience.
            </p>
            <p>
              NCG does not control or take responsibility for the content or
              practices of such websites.
            </p>
          </section>

          <section className="policy-section">
            <h4>9. Privacy</h4>
            <p>
              Your use of this website is also governed by our Privacy Policy,
              which explains how we collect and use your information.
            </p>
          </section>

          <section className="policy-section">
            <h4>10. Changes to Terms</h4>
            <p>
              NCG Corporate Engineers Pvt. Ltd. reserves the right to update or
              modify these Terms of Use at any time without prior notice.
            </p>
            <p>Users are encouraged to review this page periodically.</p>
          </section>

          <section className="policy-section">
            <h4>11. Governing Law</h4>
            <p>
              These Terms shall be governed by and interpreted in accordance with
              the laws of India, and any disputes shall be subject to the
              jurisdiction of courts located in Punjab &amp; Haryana.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

const CookieNoticeModal = ({ isOpen, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content large privacy-policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-notice-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="cookie-notice-title">Cookie Notice</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close cookie notice"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">
          <section className="policy-section">
            <h4>1. What Are Cookies?</h4>
            <p>
              Cookies are small text files that are stored on your device
              (computer, mobile, or tablet) when you visit a website. They help
              improve user experience by remembering preferences and enabling
              website functionality.
            </p>
          </section>

          <section className="policy-section">
            <h4>2. How We Use Cookies</h4>
            <p>
              At NCG Corporate Engineers Pvt. Ltd., we use cookies to:
            </p>
            <ul>
              <li>Ensure the website functions properly</li>
              <li>Improve website performance and user experience</li>
              <li>Understand how visitors interact with our website</li>
              <li>Support marketing and advertising activities</li>
            </ul>
          </section>

          <section className="policy-section">
            <h4>3. Types of Cookies We Use</h4>

            <h5 className="policy-subheading">a) Essential Cookies</h5>
            <p>
              These cookies are necessary for the website to function and cannot
              be disabled. They include features like page navigation and secure
              access.
            </p>

            <h5 className="policy-subheading">b) Performance Cookies</h5>
            <p>
              These cookies help us understand how visitors use our website
              (e.g., pages visited, time spent), allowing us to improve
              performance.
            </p>

            <h5 className="policy-subheading">c) Functional Cookies</h5>
            <p>
              These cookies remember your preferences (such as language or
              region) to enhance your experience.
            </p>

            <h5 className="policy-subheading">d) Marketing Cookies</h5>
            <p>
              These cookies may be used to deliver relevant advertisements and
              track the effectiveness of marketing campaigns (e.g., Facebook Ads,
              Google Ads).
            </p>
          </section>

          <section className="policy-section">
            <h4>4. Third-Party Cookies</h4>
            <p>
              We may allow third-party services (such as analytics tools or
              advertising platforms) to place cookies on your device. These
              providers may collect information about your online activities
              across different websites.
            </p>
          </section>

          <section className="policy-section">
            <h4>5. Managing Cookies</h4>
            <p>
              You can control or disable cookies through your browser settings.
              Most browsers allow you to:
            </p>
            <ul>
              <li>View stored cookies</li>
              <li>Delete cookies</li>
              <li>Block cookies from specific or all websites</li>
            </ul>
            <p>
              Please note that disabling cookies may affect website functionality.
            </p>
          </section>

          <section className="policy-section">
            <h4>6. Consent</h4>
            <p>
              By continuing to use our website, you consent to the use of cookies
              as described in this Cookie Notice.
            </p>
            <p>
              For users in regions such as the European Union, a cookie consent
              banner may be displayed to obtain explicit consent where required.
            </p>
          </section>

          <section className="policy-section">
            <h4>7. Updates to This Notice</h4>
            <p>
              We may update this Cookie Notice from time to time to reflect
              changes in technology, law, or our practices.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [cookieNoticeOpen, setCookieNoticeOpen] = useState(false);

  return (
    <>
      <footer className="simple-footer">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-6">
              <div className="footer-social-icons">
                <a
                  href="https://www.linkedin.com/company/nimarta-consulting-group-pvt-ltd-ncg-/?viewAsMember=true"
                  aria-label="LinkedIn"
                >
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a
                  href="https://www.instagram.com/ncg_corporate_engineers?igsh=MWJzOHAwMHdjc29ubg=="
                  aria-label="Instagram"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.youtube.com/channel/UCYZaqJKfVMCEgulGxNSBhxg"
                  aria-label="YouTube"
                >
                  <i className="fab fa-youtube"></i>
                </a>

                <a href="https://www.facebook.com/ncgcorporate" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="footer-links">
                <a href="/social-activities">Social Media Activities</a>
                <a href="/sitemap">Sitemap</a>
                <a href="/contact">Contact Us</a>
              </div>
            </div>
          </div>

          <div className="row align-items-center mt-3">
            <div className="col-lg-6 col-md-6">
              <div className="footer-company-info">
                <h6>NCG Corporate</h6>
                <p>&copy; 2024 - 2025</p>
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="footer-links">
                <button
                  type="button"
                  className="legal-link"
                  onClick={() => setAccessibilityOpen(true)}
                >
                  Accessibility Policy
                </button>
                <button
                  type="button"
                  className="legal-link"
                  onClick={() => setTermsOpen(true)}
                >
                  Terms of Use
                </button>
                <button
                  type="button"
                  className="legal-link"
                  onClick={() => setPrivacyOpen(true)}
                >
                  Privacy Policy
                </button>
                <button
                  type="button"
                  className="legal-link"
                  onClick={() => setCookieNoticeOpen(true)}
                >
                  Cookie Notice
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <PrivacyPolicyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <AccessibilityPolicyModal
        isOpen={accessibilityOpen}
        onClose={() => setAccessibilityOpen(false)}
      />
      <TermsOfUseModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
      />
      <CookieNoticeModal
        isOpen={cookieNoticeOpen}
        onClose={() => setCookieNoticeOpen(false)}
      />
    </>
  );
};

export default Footer;
